<template>
  <div ref="wrapper" class="line-chart-wrapper">
    <!-- CP-224: the canvas (and its chart.js chunk) mounts when the chart nears the viewport, not before LCP. -->
    <template v-if="nearViewport">
      <Line :chart-data="chartData" :chart-options="chartOptions" />
    </template>
  </div>
</template>

<script>
import { defineComponent, defineAsyncComponent, h, computed, onMounted, onBeforeUnmount, nextTick, ref } from 'vue';
import { whenNearViewport } from '@/utils/nearViewport';
import { lineChartOptions } from '@/utils/chartOptions';
import StaleChunkNote from './StaleChunkNote.vue';

// CP-224: chart.js + vue-chartjs (~100 KB minified) are a separate chunk that loads when the first chart mounts,
// instead of sitting in the entry bundle every page downloads and parses before it can show anything.
// The chart sits in a fixed-height box, so a canvas that arrives a moment later moves nothing (no CLS).
// One retry if the chunk request fails (flaky mobile link).
const Line = defineAsyncComponent({
  loader: () => import(/* webpackChunkName: "line-chart" */ './LineChartCanvas.js').then((m) => {
    // Same forced resize onMounted does below, for a canvas that mounts after the chunk arrives.
    setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
    return m.default;
  }),
  // CP-274: still failing after the retries (e.g. a tab from before a deploy asking for a chunk hash that is gone):
  // show the 'refresh to update' note in the chart's square (see StaleChunkNote `square`). Never an automatic reload.
  errorComponent: { name: 'LineChartStaleNote', render: () => h(StaleChunkNote, { square: true }) },
  onError(error, retry, fail, attempts) {
    if (attempts <= 2) retry(); else fail();
  }
});

export default defineComponent({
  name: 'LineChart',
  components: {
    Line
  },
  props: {
    chartData: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    // CP-248: vue-chartjs 4 reads `chartOptions` only. The caller's `options` (price formatting, label rotation,
    // padding, tooltip) used to go to an `options` attribute <Line> ignores; now defaults + caller options + theme
    // text colour are merged into the one `chartOptions` object (utils/chartOptions.js).
    // Canvas text is not styled by CSS, so the colour comes from --cs-chart-text (#666 light, #9bacb6 dark, CP-225),
    // re-read when the toggle flips <html data-theme>.
    const themeVersion = ref(0);
    let themeObserver = null;
    const wrapper = ref(null);
    const nearViewport = ref(false);
    let cancelNear = null;
    const chartOptions = computed(() => {
      void themeVersion.value;
      let color = '#666666';
      if (typeof window !== 'undefined' && window.getComputedStyle) {
        const v = window.getComputedStyle(document.documentElement).getPropertyValue('--cs-chart-text');
        if (v && v.trim()) color = v.trim();
      }
      return lineChartOptions(props.options, color);
    });

    // Force chart resize after mount to ensure proper sizing
    onMounted(async () => {
      cancelNear = whenNearViewport(wrapper.value, () => {
        nearViewport.value = true;
        // Same forced resize as below, for a canvas that mounts once the chart scrolls near (chunk already cached).
        nextTick(() => window.dispatchEvent(new Event('resize')));
      });
      if (typeof MutationObserver === 'function') {
        themeObserver = new MutationObserver(() => { themeVersion.value += 1; });
        themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
      }
      await nextTick();
      // Trigger window resize event to force Chart.js to recalculate
      window.dispatchEvent(new Event('resize'));
    });
    
    onBeforeUnmount(() => {
      if (themeObserver) themeObserver.disconnect();
      if (cancelNear) cancelNear();
    });

    return {
      wrapper,
      nearViewport,
      chartOptions
    };
  }
});
</script>

<style scoped>
.line-chart-wrapper {
  width: 100%;
  height: 100%;
  min-height: 0;
  position: relative;
  overflow: hidden;
}

:deep(.line-chart-wrapper > *) {
  width: 100% !important;
  height: 100% !important;
  min-height: 0 !important;
  max-height: 100% !important;
}

:deep(canvas) {
  max-width: 100% !important;
  width: 100% !important;
  max-height: 100% !important;
  display: block;
}
</style>
